import type { IAccount } from "@/modules/account/common/types";
import { budgetCommissionFieldKeys } from "@/modules/transaction/common/constants";
import {
  BudgetTransferKeys,
  PartyType,
  RegionCodeType,
  ResidenceType,
  TransferKeys
} from "@/modules/transaction/common/enum";
import type {
  IBudgetAccount,
  IRegionCodeDirectory,
  ITaxPayer
} from "@/modules/transaction/common/types";
import { getAccountsFilteredList } from "@/modules/transaction/helpers";
import {
  useLazyGetBudgetAccountQuery,
  useLazyGetRegionCodesQuery,
  useLazyGetTaxPayersQuery
} from "@/modules/transaction/services";
import { Currencies, ErrorMessages, Storage } from "@/shared/common/enums";
import type { IObject, IOption } from "@/shared/common/types";
import { getFromLocalStorage, isValidAccount, isValidAccountChecksum } from "@/shared/helpers";
import { useNotify } from "@/shared/hooks";
import { debounce } from "@/shared/utils";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type Dispatch, type SetStateAction, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  accountsList: IAccount[];
  setIsTinValid: Dispatch<SetStateAction<boolean>>;
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

export const useBudgetTransfer = ({
  setIsTinValid, accountsList, setIsAccountNumberInvalid
}: Props) => {
  const { t } = useTranslation();
  const [regionCodeOptions, setRegionCodeOptions] = useState<IOption[]>([]);
  const [isTaxAccount, setIsTaxAccount] = useState<boolean>(false);
  const [taxPayers, setTaxPayers] = useState<ITaxPayer | null>(null);
  const [hasWarning, setHasWarning] = useState<boolean>(false);
  const [budgetAccount, setBudgetAccount] = useState<IBudgetAccount | null>(null);

  const prevTinRef = useRef<string>("");
  const prevCreditAccountRef = useRef<string>("");
  const prevRegionCodeTypeRef = useRef<string>("");

  const userTin = getFromLocalStorage(Storage.Tin);

  const { setValue, watch, errors, reset, setError, clearErrors } = useFormProps();
  const {
    clearAlertNotification,
    showErrorAlertNotification,
    showWarningAlertNotification,
  } = useNotify();

  const [getRegionCodes, { data: regionCodes }] = useLazyGetRegionCodesQuery();
  const [getTaxPayers] = useLazyGetTaxPayersQuery();
  const [getBudgetAccount] = useLazyGetBudgetAccountQuery();

  const values = watch?.() || {};

  const generateRegionCodeOptions = (codes: IRegionCodeDirectory[]) => {
    const regionCodes = codes.map((item) => ({
      label: `${item.id} • ${item.description}`,
      value: item.id,
      meta: item.type,
    }));
    setRegionCodeOptions(regionCodes);
  };

  const handleGetRegionCodes = (regionCodeType?: string) => {
    const type = regionCodeType || budgetAccount?.regionCodeType;
    if (!type) return;
    if (type === prevRegionCodeTypeRef.current && regionCodes?.length) {
      generateRegionCodeOptions(regionCodes);
      return;
    }
    prevRegionCodeTypeRef.current = type;
    getRegionCodes(type)
      .unwrap()
      .then((codes) => {
        generateRegionCodeOptions(codes);
      });
  };

  const handleGetTaxPayers = () => {
    const values = watch?.();
    const tin = values?.isAuthorizedTransfer ? values?.authorizedBudgetTransferParty.tin || "" : userTin;
    if (!tin) return;
    if (tin === prevTinRef.current && taxPayers?.regionCode) {
      setValue(BudgetTransferKeys.RegionCode, taxPayers?.regionCode);
      return;
    }
    prevTinRef.current = tin;
    setIsTinValid(true);
    getTaxPayers(tin).unwrap()
      .then((res) => {
        setTaxPayers(res);
        setValue(BudgetTransferKeys.RegionCode, res.regionCode);
      })
      .catch((err) => {
        prevTinRef.current = "";
        if (values?.isAuthorizedTransfer) {
          setIsTinValid(false);
        } else {
          if (err.status === 404) {
            showWarningAlertNotification(ErrorMessages.InvalidOwnTin);
            setHasWarning(true);
          } else {
            showErrorAlertNotification(ErrorMessages.InvalidTin);
          }
        }
      });
  };

  const handleGetBudgetAccounts = (account: string) => {
    if (account === prevCreditAccountRef.current) return;

    const partyType = watch?.(BudgetTransferKeys.PartyType);
    const isAuthorizedTransfer = watch?.(BudgetTransferKeys.IsAuthorizedTransfer);

    if (prevCreditAccountRef.current) {
      setValue(BudgetTransferKeys.RegionCode, "");
    }
    prevCreditAccountRef.current = account;
    setIsAccountNumberInvalid(false);
    setBudgetAccount(null);
    clearErrors?.(BudgetTransferKeys.CreditBudgetAccount);
    getBudgetAccount(account).unwrap().then((res) => {
      setBudgetAccount(res);
      const isTax = res.regionCodeType === RegionCodeType.Tax;
      setIsTaxAccount(isTax);
      if (!isAuthorizedTransfer) {
        if (isTax) {
          handleGetTaxPayers();
        } else {
          handleGetRegionCodes(res.regionCodeType);
        }
        return;
      }

      if ((isTax && partyType === PartyType.NaturalPerson) || (!isTax && partyType)) {
        handleGetRegionCodes(res.regionCodeType);
      } else if (partyType === PartyType.LegalEntity || partyType === PartyType.PrivateEntrepreneur) {
        handleGetTaxPayers();
      }
    }).catch(() => {
      setRegionCodeOptions([]);
      setIsAccountNumberInvalid(true);
      setError?.(
        BudgetTransferKeys.CreditBudgetAccount,
        { message: t(ErrorMessages.UnknownAccountHolder) }
      );
    });
  };

  const handleClickAuthorizedTransfer = (isChecked: boolean) => {
    const residency = watch?.(BudgetTransferKeys.Residency);
    setValue(BudgetTransferKeys.RegionCode, "");
    if (!isChecked && hasWarning) {
      showWarningAlertNotification(ErrorMessages.InvalidOwnTin);
    } else {
      clearAlertNotification();
    }
    if (isChecked && !residency) {
      setValue(BudgetTransferKeys.Residency, ResidenceType.Resident);
    }
    if (isTaxAccount) {
      handleGetTaxPayers();
    } else {
      handleGetRegionCodes();
    }
  };

  const handleGetRegionCodeInfo = () => {
    const values = watch?.();
    const partyType = values?.authorizedBudgetTransferParty?.partyType;
    const isAuthorizedTransfer = values?.isAuthorizedTransfer;
    const tin = values?.authorizedBudgetTransferParty?.tin;
    setValue(BudgetTransferKeys.RegionCode, "");
    if (isAuthorizedTransfer) {
      if ((isTaxAccount && partyType === PartyType.NaturalPerson) || (!isTaxAccount && partyType)) {
        handleGetRegionCodes();
      } else if (
        tin.length === 8 && (
          partyType === PartyType.LegalEntity ||
          partyType === PartyType.PrivateEntrepreneur
        )
      ) {
        handleGetTaxPayers();
      }
    }
  };

  const handleClickTarget = (target: SingleValue<TOption>) => {
    const { currency } = target?.meta as IAccount;
    setValue(TransferKeys.Currency, currency);
  };

  const handleChangePartType = (partyType: SingleValue<TOption>) => {
    if (partyType?.value === PartyType.NaturalPerson) {
      setValue(BudgetTransferKeys.Tin, "");
    } else if (partyType?.value === PartyType.LegalEntity || partyType?.value === PartyType.PrivateEntrepreneur) {
      setValue(BudgetTransferKeys.Ssn, "");
    }
    handleGetRegionCodeInfo();
  };

  const getBudgetAccountInfo = useMemo(
    () =>
      debounce((account: string) => {
        handleGetBudgetAccounts(account);
      }, 700),
    []
  );

  const changes = watch?.(budgetCommissionFieldKeys).toString();

  const fieldChanges = useMemo(() => {
    const values: IObject = {};
    budgetCommissionFieldKeys.forEach(
      (fieldKey: string) => values[fieldKey] = watch?.(fieldKey)
    );
    return values;
  }, [changes]);

  const canGetCommission = useMemo(() => {
    const creditBudgetAccountStr = fieldChanges.creditBudgetAccount ? String(fieldChanges.creditBudgetAccount) : "";
    return !!(
      fieldChanges.debitAccount &&
      fieldChanges.creditBudgetAccount &&
      isValidAccountChecksum(creditBudgetAccountStr) &&
      isValidAccount(creditBudgetAccountStr) &&
      fieldChanges.amount
    );
  }, [fieldChanges]);

  const accountsFilteredList = useMemo(() => {
    return getAccountsFilteredList({
      accountsList,
      accountFilters: [Currencies.AMD]
    });
  }, [accountsList]);


  return {
    reset,
    watch,
    errors,
    values,
    userTin,
    setValue,
    setError,
    taxPayers,
    hasWarning,
    fieldChanges,
    isTaxAccount,
    budgetAccount,
    setBudgetAccount,
    canGetCommission,
    handleClickTarget,
    regionCodeOptions,
    prevCreditAccountRef,
    accountsFilteredList,
    getBudgetAccountInfo,
    handleChangePartType,
    handleGetRegionCodeInfo,
    handleClickAuthorizedTransfer,
  };
};
